import React, { useState, useEffect } from 'react';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth, forceReinitializeFirebase } from '../firebase/config';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Get return URL from location state
  const returnUrl = location.state?.returnUrl || '/account';
  const isPlanning = location.state?.isPlanning || false;
  const message = location.state?.message || '';

  useEffect(() => {
    if (user) {
      console.log('🔀 LoginPage: User authenticated, redirecting...', {
        isPlanning,
        returnUrl,
        hasSavedState: !!sessionStorage.getItem('pendingTripFormState')
      });
      
      // If user was trying to plan a trip, navigate to homepage
      // Otherwise, go to the specified return URL
      if (isPlanning) {
        // Check if they were trying to access AI assist specifically
        if (returnUrl === '/ai-assist') {
          console.log('🎯 LoginPage: Redirecting to AI assist');
          navigate('/ai-assist');
        } else {
          console.log('🏠 LoginPage: Redirecting to homepage');
          navigate('/');
        }
      } else {
        console.log('📍 LoginPage: Redirecting to:', returnUrl);
        navigate(returnUrl);
      }
    }
  }, [user, navigate, returnUrl, isPlanning]);

  const handleGoogleLogin = async () => {
    setError('');
    setIsLoading(true);

    try {
      console.log('Starting Google sign-in...');
      console.log('Auth object:', auth);
      console.log('Current domain:', window.location.origin);

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      console.log('Provider created:', provider);
      
      if (!auth) {
        throw new Error('Firebase Auth is not initialized');
      }
      
      const result = await signInWithPopup(auth, provider);
      console.log('Login successful:', result.user);
      
      // Navigation will be handled by useEffect when user state updates
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // Handle API key errors specifically
      if (error.code === 'auth/api-key-not-valid' || error.message.includes('api-key-not-valid')) {
        console.log('🔄 API key error detected, clearing cache and reinitializing Firebase...');
        try {
          // Clear all Firebase cache
          if (typeof window !== 'undefined') {
            window.sessionStorage.clear();
            window.localStorage.removeItem('firebaseConfigCache');
            window.localStorage.removeItem('firebaseConfigCache_v2');
            window.localStorage.removeItem('firebaseConfigCache_v3');
          }
          
          await forceReinitializeFirebase();
          setError('Firebase configuration was refreshed. Please try signing in again.');
        } catch (reinitError) {
          console.error('Failed to reinitialize Firebase:', reinitError);
          setError('Firebase configuration error. Please refresh the page and try again.');
        }
      } else if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup was blocked. Please allow popups and try again.');
      } else if (error.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized for Google sign-in.');
      } else if (error.code === 'auth/operation-not-allowed') {
        setError('Google sign-in is not enabled for this app.');
      } else {
        setError(`Failed to sign in with Google: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };


  if (user) {
    return <div>Redirecting...</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {isPlanning ? 'Sign in to continue planning' : 'Sign in to your account'}
          </h2>
          {message && (
            <p className="mt-2 text-center text-sm text-blue-600 bg-blue-50 rounded-md py-2 px-3">
              {message}
            </p>
          )}
          {!isPlanning && (
            <p className="mt-2 text-center text-sm text-gray-600">
              Access your travel plans and start your next adventure
            </p>
          )}
        </div>
        
        <div className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {isLoading ? 'Signing in...' : 'Continue with Google'}
          </button>
          
          {isPlanning && (
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                ← Back to homepage
              </button>
            </div>
          )}
          
          {/* Debug button for Firebase cache issues */}
          {error && error.includes('Firebase') && (
            <div className="mt-4 text-center">
              <button
                onClick={async () => {
                  try {
                    await forceReinitializeFirebase();
                    setError('');
                    console.log('Firebase cache cleared and reinitialized');
                  } catch (err) {
                    console.error('Failed to clear Firebase cache:', err);
                  }
                }}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                🔄 Clear Firebase cache and retry
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;